import { useState } from 'react'

import {
	Box,
	Button,
	FormControlLabel,
	Radio,
	RadioGroup,
	TextField,
	Typography,
} from '@mui/material'
import { GAME_TYPE } from 'constants/enums/GameTypeEnum'

import { TEST_ID as PAGE_TEST_ID } from 'pages/BattleShips'

import ButtonWithIconComponent from 'components/ButtonWithIcon'
import PromptBox from 'components/PromptBoxV2'
import Section2Title from 'components/Section2Title'
import UserEducationModal from 'components/UserEducationModal'

import editIcon from 'assets/images/editIcon.svg'
import refreshIcon from 'assets/images/refreshIcon.svg'

import { commonStyles } from 'styles/common.styles'

import { styles } from './PromptSection.styles'

export const TEST_ID = 'battle-ships-prompt-section'

export type Props = {
	disruptionType?: string
	generateNewScenarioDisabled: () => boolean
	handleClick: (overrideDisruptionType?: string) => Promise<void>
	isLoading: boolean
	onDisruptionTypeChange?: (type: string) => void
	prompt: string
	reloadIdeas: (newPrompt: string) => Promise<void>
}

const PromptSection = ({
	prompt,
	isLoading,
	generateNewScenarioDisabled,
	handleClick,
	reloadIdeas,
	onDisruptionTypeChange,
	disruptionType: externalDisruptionType,
}: Props) => {
	const [isEditMode, setIsEditMode] = useState(false)
	const [internalDisruptionType, setInternalDisruptionType] =
		useState('marketplace') // marketplace or future
	const disruptionType = externalDisruptionType || internalDisruptionType

	const handleSaveButtonClick = async () => {
		const textareaPrompt = document.getElementsByTagName('textarea')

		if (textareaPrompt && textareaPrompt.length > 0) {
			setIsEditMode(false)
			await reloadIdeas(textareaPrompt[0].value)
		}
	}

	const onClickCancelEdit = () => {
		setIsEditMode(false)
	}

	return (
		<>
			<Section2Title />
			<UserEducationModal />
			<Box display={'flex'}>
				<Box marginRight={'2rem'} sx={styles.sideBar} />
				<Box sx={styles.container}>
					<Box sx={styles.innerColumn}>
						<Box
							display="flex"
							justifyContent="space-between"
							sx={styles.headerBox}
						>
							<Typography sx={commonStyles.header}>
								What would happen if your company suddenly
								experienced one of the following scenarios?
							</Typography>
							<ButtonWithIconComponent
								disabled={
									generateNewScenarioDisabled() || isLoading
								}
								iconSvg={refreshIcon}
								text="REFRESH"
								data-testid={`${PAGE_TEST_ID}-prompt-section__prompt-box_main-refresh`}
								onClickAction={handleClick}
							/>
						</Box>
						<Box display={'flex'} flexDirection={'row'}>
							<RadioGroup
								aria-label="disruption-type"
								name="disruption-type"
								value={disruptionType}
								onChange={async (event) => {
									const newType = event.target.value
									if (onDisruptionTypeChange) {
										onDisruptionTypeChange(newType)
										// Trigger new API call when radio button changes to update the scenario text
										// Pass newType directly to avoid race condition with state update
										if (prompt && !isLoading) {
											await handleClick(newType)
										}
									} else {
										setInternalDisruptionType(newType)
									}
								}}
								sx={styles.radioGroup}
								row
							>
								<FormControlLabel
									value="marketplace"
									control={<Radio />}
									label={
										<Typography variant="body2">
											MARKETPLACE DISRUPTION
										</Typography>
									}
									data-testid={`${TEST_ID}__marketplace-radio`}
								/>
								<Typography
									variant="body2"
									data-testid={`${TEST_ID}__support-text`}
									sx={{ width: '4.9rem' }}
								>
									{'OR'}
								</Typography>
								<FormControlLabel
									value="future"
									control={<Radio />}
									label={
										<Typography variant="body2">
											FUTURE TREND ADOPTION
										</Typography>
									}
									data-testid={`${TEST_ID}__future-radio`}
								/>
							</RadioGroup>
						</Box>
						{isEditMode ? (
							<>
								<TextField
									data-testid={`${PAGE_TEST_ID}-prompt-section__edit-company-disruption`}
									defaultValue={prompt}
									multiline
									fullWidth
									inputRef={(input) => input && input.focus()}
								/>

								<Box sx={styles.editButtonsContainer}>
									<Button
										color="secondary"
										variant="contained"
										data-testid={`${PAGE_TEST_ID}-prompt-section__edit-save-button`}
										onClick={async () =>
											await handleSaveButtonClick()
										}
									>
										<Typography>SAVE CHANGES</Typography>
									</Button>
									<Button
										variant="text"
										color="secondary"
										data-testid={`${PAGE_TEST_ID}-prompt-section__edit-cancel-button`}
										onClick={onClickCancelEdit}
									>
										<Typography>CANCEL</Typography>
									</Button>
								</Box>
							</>
						) : (
							<>
								<PromptBox
									gameTypeEnum={GAME_TYPE.BATTLE_SHIPS}
									contentText={
										prompt || 'Generate Scenario Above'
									}
									data-testid="company-disruption"
									loading={isLoading}
									hidePromptContainerStyle={true}
								/>
								<Box sx={styles.rightAlign}>
									<ButtonWithIconComponent
										disabled={
											generateNewScenarioDisabled() ||
											isLoading
										}
										iconSvg={editIcon}
										text="EDIT"
										data-testid={`${PAGE_TEST_ID}-prompt-section-edit-prompt`}
										onClickAction={() =>
											setIsEditMode(!isEditMode)
										}
									/>
								</Box>
							</>
						)}
					</Box>
				</Box>
			</Box>
		</>
	)
}

export default PromptSection
